import Badge, { BadgeColor } from "@/components/Badge";
import Icon from "@/components/Icon";
import type { MaterialNames } from "@/components/material_symbol_names";
import { Path, useNavigate } from "@/router";
import { PropsWithChildren, ReactNode } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { LitPrimaryTab } from "./LitTabs";

export interface TabItem {
  label: string;
  active?: boolean;
  onClick?(): void;
  link?: Path;
  badgeValue?: number;
  badgeColor?: BadgeColor;
  iconName?: MaterialNames;
  icon?: ReactNode;
  iconOnly?: boolean;
  testId?: string;
}

/**
 * Props for the Tabs component
 * @property {string} [className] - Optional className for additional styling
 * @property {Array} tabItems - Array of tab item configurations
 * @property {string} tabItems[].label - The label text for the tab
 * @property {MaterialNames} [tabItems[].iconName] - Optional Material icon beside the label, or alone when `iconOnly` is true
 * @property {ReactNode} [tabItems[].icon] - Optional custom icon node beside the label, or alone when `iconOnly` is true
 * @property {boolean} [tabItems[].iconOnly] - When true with `iconName` or `icon`, shows only the icon (label remains for `aria-label`)
 * @property {string} [tabItems[].testId] - Optional `data-testid` for the tab button (defaults to `"tab"`)
 * @property {boolean} [tabItems[].active] - Whether the tab is active
 * @property {Function} [tabItems[].onClick] - Optional click handler
 * @property {Path} [tabItems[].link] - Optional navigation path
 * @property {number} [tabItems[].badgeValue] - Optional badge value to display on the tab (including icon-only tabs). When provided, shows a label badge with the number. If only badgeColor is set, shows a dot badge.
 * @property {BadgeColor} [tabItems[].badgeColor] - Optional badge color. When set, shows a badge (dot if no value, label if value provided). Defaults to primary.
 * @property {ReactNode} [actionIcon] - Optional icon button to display. On desktop, positioned at the right side of the dividing line. On mobile, positioned immediately to the right of the last tab.
 */
export interface TabsProps {
  className?: string;
  tabItems: TabItem[];
  actionIcon?: ReactNode;
  showWhenSingle?: boolean;
}

const isTabActive = (currentPath: string, linkTo?: Path, active?: boolean) => {
  if (!linkTo || typeof active === "boolean") {
    return active;
  }

  let path = currentPath;

  while (path && path !== "/") {
    if (matchPath(linkTo, path)) {
      return true;
    }

    path = path.split("/").slice(0, -1).join("/");
  }

  return false;
};

export default function Tabs({
  className,
  tabItems,
  actionIcon,
  showWhenSingle = false
}: PropsWithChildren<TabsProps>) {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't render tabs if there's only one or zero tab items
  if (!tabItems || (!showWhenSingle && tabItems.length <= 1)) {
    return null;
  }

  const renderedTabs = tabItems.map(
    ({ label, active, onClick, link, badgeValue, badgeColor, iconName, icon, iconOnly, testId }, i) => {
      const isActive = isTabActive(location.pathname, link, active);
      const tabClassName = isActive ? "text-body-large-bold" : "text-body-large-semibold";
      const shouldShowBadge = badgeValue !== undefined || badgeColor !== undefined;
      const showIcon = Boolean(iconName || icon);
      const showLabel = !iconOnly || !showIcon;

      // md-primary-tab stacks icon+label at 64px unless inlineIcon is set. Mixing
      // that with a label-only tab (home Roster) lifts the indicator off the baseline.
      return (
        <LitPrimaryTab
          key={i}
          active={isActive}
          className={`${tabClassName} shrink-0`}
          onClick={link ? () => navigate(link) : onClick}
          data-testid={testId ?? "tab"}
          hasIcon={showIcon}
          iconOnly={Boolean(iconOnly && showIcon)}
          inlineIcon
          aria-label={iconOnly ? label : undefined}
        >
          {showIcon ? (
            <span slot="icon" className="relative flex items-center justify-center overflow-visible">
              {icon ?? (iconName ? <Icon name={iconName} size={20} className="shrink-0" /> : null)}
              {!showLabel && shouldShowBadge ? (
                <span className="pointer-events-none absolute -right-1.5 -top-1.5">
                  <Badge value={badgeValue} color={badgeColor} />
                </span>
              ) : null}
            </span>
          ) : null}
          {showLabel ? (
            <span className="flex items-center gap-2">
              {label}
              {shouldShowBadge ? <Badge value={badgeValue} color={badgeColor} /> : null}
            </span>
          ) : null}
        </LitPrimaryTab>
      );
    }
  );

  return (
    <div
      className={`mt-8 flex min-w-0 border-b border-extended-pgadarknavy-brand-color ${
        actionIcon ? "items-center" : "overflow-x-auto"
      } ${className}`}
    >
      {actionIcon ? (
        <>
          <div className="flex min-w-0 flex-1 overflow-x-auto">
            {renderedTabs}
            <div className="flex items-center md:hidden">{actionIcon}</div>
          </div>
          <div className="ml-auto hidden shrink-0 items-center md:flex">{actionIcon}</div>
        </>
      ) : (
        renderedTabs
      )}
    </div>
  );
}
